import { collection, getDocs, type Timestamp } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

/**
 * Every platform invite (D21), as a super admin sees it.
 *
 * The event-scoped mirror behind `useEventInvites` shows an event admin the
 * invites minted under their own event. This is the whole collection, which
 * the rules open to a super admin alone.
 *
 * The shareable token is deliberately absent, here as there: the document key
 * is sha256 of the token, precisely so a list of invites is not a list of
 * working credentials. The token exists only in the mint outcome its minter
 * just received.
 */
export interface PlatformInvite {
  id: string;
  label: string | undefined;
  maxUses: number;
  issued: number;
  uses: number;
  expiresAt: Timestamp | undefined;
  revokedAt: Timestamp | undefined;
  createdBy: string;
  contextEventId: string | undefined;
  contextEventSlug: string | undefined;
}

type PlatformInviteState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; invites: PlatformInvite[] };

export function usePlatformInvites(): {
  state: PlatformInviteState;
  reload: () => void;
} {
  const [state, setState] = useState<PlatformInviteState>({
    status: "loading",
  });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      setInvites(await getDocs(collection(db, "platform_invites")), setState);
    } catch (err) {
      setState({
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load invites",
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Stable across renders. A fresh closure here re-fires the caller's
  // reload-on-success effect, which reloads, which renders, which makes
  // another closure: the list never settles and the page errors until reload.
  const reload = useCallback(() => void load(), [load]);

  return { state, reload };
}

type InviteSnapshot = {
  id: string;
  get: (field: string) => unknown;
};

function setInvites(
  snapshot: { docs: InviteSnapshot[] },
  setState: React.Dispatch<React.SetStateAction<PlatformInviteState>>,
): void {
  setState({
    status: "success",
    invites: snapshot.docs
      .map((document) => ({
        id: document.id,
        label: document.get("label") as string | undefined,
        maxUses: document.get("maxUses") as number | undefined,
        issued: document.get("issued") as number | undefined,
        uses: document.get("uses") as number | undefined,
        expiresAt: document.get("expiresAt") as Timestamp | undefined,
        revokedAt: (document.get("revokedAt") ?? undefined) as
          Timestamp | undefined,
        createdBy: document.get("createdBy") as string | undefined,
        contextEventId: document.get("contextEventId") as string | undefined,
        contextEventSlug: document.get("contextEventSlug") as
          string | undefined,
      }))
      // A malformed field reads as the least it can be: the cap stays the cap,
      // and a count that is missing is nothing rather than an error. Such a row
      // still shows, which is what a listing is for.
      .map((invite) => ({
        ...invite,
        maxUses: invite.maxUses ?? 0,
        issued: invite.issued ?? 0,
        uses: invite.uses ?? 0,
        createdBy: invite.createdBy ?? "",
      }))
      // Newest first, ordered client-side so no index is owed.
      .sort(
        (a, b) =>
          (b.expiresAt?.toMillis() ?? 0) - (a.expiresAt?.toMillis() ?? 0),
      ),
  });
}
